import {
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  User,
  getIdToken
} from 'firebase/auth';
import { auth } from '../config/firebase.config';
import HttpService from './http.service';

// Servicio de autenticación con Firebase
export const FirebaseAuthService = {
  // Iniciar sesión anónima para onboarding
  signInAnonymously: async () => {
    try {
      const result = await signInAnonymously(auth);
      const user = result.user;
      return user;
    } catch (error) {
      throw error;
    }
  },

  // Iniciar sesión con Google
  signInWithGoogle: async (isCompanyCreation = false) => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await getIdToken(user);
      localStorage.setItem('firebase_token', idToken);
      localStorage.setItem('is_company_creation', isCompanyCreation ? 'true' : 'false');

      try {
        const response = await HttpService.post('/api/auth/firebase-login', { firebaseToken: idToken });
        if (response && response.data && response.data.token) localStorage.setItem('auth_token', response.data.token);

        if (response && response.data) {
          localStorage.setItem('is_new_user', response.data.isNewUser ? 'true' : 'false');
          if (response.data.user && response.data.user.state) localStorage.setItem('user_state', response.data.user.state);
          //if (response.data.user.state === 'pending')  localStorage.removeItem('auth_token');

          // Guardar el ID del usuario si está disponible
          if (response.data.user && response.data.user.id) {
            localStorage.setItem('user_id', response.data.user.id);
          } else if (response.data.user && response.data.user._id) {
            localStorage.setItem('user_id', response.data.user._id);
          } else if (response.data.userId) {
            localStorage.setItem('user_id', response.data.userId);
          }

          if (response.data.user && response.data.user.role) {
            localStorage.setItem('user_role', response.data.user.role);
          } else if (isCompanyCreation) {
            localStorage.setItem('user_role', 'company');
          } else {
            localStorage.setItem('user_role', 'user');
          }
        }

        if (response.data.user) {
          if (isCompanyCreation) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('firebase_token');
            localStorage.removeItem('userProfileImage');
            localStorage.removeItem('user_state');
            localStorage.removeItem('is_company_creation');

            await signOut(auth);
            // Devolver el usuario pero con una bandera especial para que el componente sepa que debe mostrar el modal
            return { ...user, accountExists: true };
          } else {
            localStorage.setItem('user_state', response?.data?.user?.state);
            localStorage.removeItem('show_pending_verification');
            localStorage.setItem('auth_token', response?.data?.token);

            window.dispatchEvent(new CustomEvent('auth-success', {
              detail: { user, state: response?.data?.user?.state }
            }));
            return user;
          }
        }
      } catch (backendError: any) {
        // Verificar si es un error 401 (usuario pendiente de verificación)
        if (backendError.response && backendError.response.status === 401) {
          localStorage.setItem('user_state', backendError?.response?.data?.user?.state);
          localStorage.removeItem('show_pending_verification');
          localStorage.removeItem('auth_token');
          await signOut(auth);
          throw new Error('UNAUTHORIZED');
        }
        throw backendError;
      }

      return user;
    } catch (error) {
      throw error;
    }
  },

  // Cerrar sesión
  signOut: async () => {
    try {
      // Limpiar cache de imágenes si hay una imagen de perfil
      const profileImageUrl = localStorage.getItem('userProfileImage');
      if (profileImageUrl) {
        // Crear un timestamp para forzar recarga de la imagen en futuras sesiones
        localStorage.setItem('last_logout_time', Date.now().toString());
      }

      await signOut(auth);
      // Eliminar todos los tokens y datos de autenticación
      localStorage.removeItem('auth_token');
      localStorage.removeItem('is_new_user');
      localStorage.removeItem('user_state');
      localStorage.removeItem('user_role'); // Eliminar el rol del usuario
      localStorage.removeItem('userProfileImage'); // Eliminar la imagen de perfil
      localStorage.removeItem('user_info'); // Eliminar la información del usuario en estado global
      localStorage.removeItem('firebase_token'); // Eliminar el token de Firebase

      // Limpiar cualquier caché de imágenes
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          if (cacheName.includes('image')) {
            caches.delete(cacheName);
          }
        });
      }).catch(err => console.error('Error al limpiar caché:', err));

      // Emitir evento de cierre de sesión para actualizar componentes
      window.dispatchEvent(new CustomEvent('auth-logout'));

    } catch (error) {
      throw error;
    }
  },

  // Obtener el usuario actual
  getCurrentUser: () => {
    return auth.currentUser;
  },

  // Observar cambios en el estado de autenticación
  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Si hay un usuario autenticado en Firebase pero no hay token JWT en localStorage
        // o si estamos en una recarga de página, intentamos obtener un nuevo token del backend
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          try {
            const idToken = await getIdToken(user);

            // Enviar el token al backend para verificación y obtener un JWT
            const response = await HttpService.post('/api/auth/firebase-login', { firebaseToken: idToken });

            if (response && response.data && response.data.token) {
              localStorage.setItem('auth_token', response.data.token);

              // Guardar información del usuario si está disponible
              if (response.data.user) {
                if (response.data.user.state) {
                  localStorage.setItem('user_state', response.data.user.state);
                }
                if (response.data.user.role) {
                  localStorage.setItem('user_role', response.data.user.role);
                }
                // Guardar el ID del usuario si está disponible
                if (response.data.user.id) {
                  localStorage.setItem('user_id', response.data.user.id);
                } else if (response.data.user._id) {
                  localStorage.setItem('user_id', response.data.user._id);
                }
              }

              // Verificar si el ID del usuario está disponible en otra parte de la respuesta
              if (response.data.userId) {
                localStorage.setItem('user_id', response.data.userId);
              }
            }
          } catch (error) {
            console.error('Error al obtener nuevo token JWT:', error);
          }
        }
      }

      // Llamar al callback original con el usuario
      callback(user);
    });
  },

  // Refrescar el token
  refreshToken: async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        // Obtener un token fresco de Firebase
        const idToken = await getIdToken(user, true);

        // Guardar el token en localStorage
        localStorage.setItem('auth_token', idToken);
        return idToken;
      } catch (error) {
        throw error;
      }
    }
    return null;
  },

  // Obtener el token de backend para un usuario
  getTokenForUser: async () => {
    try {
      const idToken = localStorage.getItem('firebase_token');
      if (!idToken) throw new Error('No se encontró el token de Firebase');

      const response = await HttpService.post('/api/auth/firebase-login', { firebaseToken: idToken });
      if (response && response?.data?.token) localStorage.setItem('auth_token', response?.data?.token);
      if (response?.data?.user) localStorage.setItem('user_id', response?.data?.user?.id);
      return response?.data?.token || null;
    } catch (error) {
      throw error;
    }
  },
};

export default FirebaseAuthService;
